import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Clock, Navigation, Star } from 'lucide-react';
import { siteInfoAPI } from '@/lib/api';
import { useTranslation } from '@/lib/i18n';

const FAVICON_URL = "https://customer-assets.emergentagent.com/job_multi-watt-hub/artifacts/q9v3nz6d_favicon-riding-watt.png";

export const Footer = () => {
  const [siteInfo, setSiteInfo] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    siteInfoAPI.getAll().then(r => setSiteInfo(r.data)).catch(() => {});
  }, []);

  return (
    <footer className="bg-[#0A0A0A] border-t border-[#1A1A1A] text-slate-400 relative" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          <div>
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img src={FAVICON_URL} alt="Riding Watt" className="w-9 h-9 rounded-lg" />
              <span className="font-heading text-lg font-bold text-white tracking-tight">
                RIDING<span className="text-[#00FF00]">WATT</span>
              </span>
            </Link>
            <p className="text-sm text-slate-500 leading-relaxed">
              The Power of Fun. {t('home.hero_badge')}.
            </p>
            <div className="flex gap-3 mt-4">
              {siteInfo.map(s => [
                s.instagram_url && <a key={`ig-${s.id}`} href={s.instagram_url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#00FF00] transition-colors text-xs">Instagram</a>,
                s.facebook_url && <a key={`fb-${s.id}`} href={s.facebook_url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#00FF00] transition-colors text-xs">Facebook</a>,
                s.tiktok_url && <a key={`tt-${s.id}`} href={s.tiktok_url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:text-[#00FF00] transition-colors text-xs">TikTok</a>,
              ]).flat().filter(Boolean).slice(0, 3)}
            </div>
          </div>

          <div>
            <h4 className="font-heading text-sm font-bold text-[#00FF00] uppercase tracking-widest mb-4">Liens rapides</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/" className="hover:text-[#00FF00] transition-colors">Accueil</Link></li>
              <li><Link to="/gift-cards" className="hover:text-[#00FF00] transition-colors">Cartes Cadeaux</Link></li>
              <li><Link to="/credits" className="hover:text-[#00FF00] transition-colors">Crédits</Link></li>
              <li><Link to="/cgv" className="hover:text-[#00FF00] transition-colors">CGV</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-sm font-bold text-[#00FF00] uppercase tracking-widest mb-4">{t('footer.our_sites')}</h4>
            <ul className="space-y-4 text-sm">
              {siteInfo.map(s => {
                const wazeUrl = s.address ? `https://waze.com/ul?q=${encodeURIComponent(s.address)}` : null;
                return (
                  <li key={s.id}>
                    <div className="flex items-start gap-2">
                      <MapPin className="w-4 h-4 mt-0.5 text-[#00FF00] flex-shrink-0" />
                      <div>
                        <span className="text-white font-medium">{s.city}</span>
                        {s.phone && (
                          <a href={`tel:${s.phone}`} className="block text-xs text-slate-400 hover:text-[#00FF00] transition-colors mt-0.5">
                            <Phone className="w-3 h-3 inline mr-1" />{s.phone}
                          </a>
                        )}
                        {wazeUrl && (
                          <a href={wazeUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-slate-500 hover:text-[#00FF00] transition-colors mt-1">
                            <Navigation className="w-3 h-3" /> {s.address}
                          </a>
                        )}
                        {!wazeUrl && s.address && (
                          <p className="text-xs text-slate-500 mt-0.5">{s.address}</p>
                        )}
                        {s.opening_hours && (
                          <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Clock className="w-3 h-3" /> {s.opening_hours}</p>
                        )}
                        {s.google_review_url && (
                          <a href={s.google_review_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-yellow-400 hover:text-yellow-300 transition-colors mt-1">
                            <Star className="w-3 h-3" /> Google Reviews
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
              {siteInfo.length === 0 && (
                <>
                  <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#00FF00] flex-shrink-0" /><span>Hyères</span></li>
                  <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 text-[#00FF00] flex-shrink-0" /><span>Toulouse</span></li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-[#1A1A1A] mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-xs text-slate-600">&copy; {new Date().getFullYear()} Riding Watt - The Power of Fun. {t('footer.rights')}.</p>
          <div className="flex gap-6 text-xs text-slate-600">
            <Link to="/cgv" className="hover:text-[#00FF00] transition-colors" data-testid="footer-cgv">CGV</Link>
            <Link to="/mentions-legales" className="hover:text-[#00FF00] transition-colors" data-testid="footer-mentions-legales">Mentions légales</Link>
            <Link to="/politique-cookies" className="hover:text-[#00FF00] transition-colors" data-testid="footer-cookies">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
